import React, { Fragment, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import pluralize from 'pluralize';

import PriceDisplay from '../../../../../../../../../components/global/PriceDisplay';
import Badge from '../../../../../../../../../components/global/Badge';
import { DIGITAL_DOWNLOADS_MODE } from '../../../../../../../../../constants/print-packages';
import { getAsset } from '../../../../../../../../../util/assets';
import PriceTagPhotoUpsellModal from '../AlbumPrintPackageOption/PriceTagPhotoUpsellModal';
import { AlbumMembershipGroupTaggedPhotosModal } from '../AlbumMembershipGroupTaggedPhotos';
import { getAlbumPrintPackageTaggedItem } from '../../helpers';
import {
  customPrintRequiresConfiguration,
  getPrintPackagePrintBackgrounds,
  printPackageRequiresConfiguration,
} from '../../../../../../../../../util/print-packages';
import AccountCardAlbumCustomPrintConfiguratorModal from '../AccountCardAlbumCustomPrintOption/AccountCardAlbumCustomPrintConfigurator/modal';
import Button from '../../../../../../../../../components/global/Button';
import { PrintProductConfiguratorModal } from '../../../../../../../../../services/prints/components/PrintProductConfigurator';
import PrintPackageConfigurableItemCompleteModal from '../AlbumPrintPackageOption/components/PrintPackageConfigurableItemCompleteModal';
import PrintPackageConfigurableItemsListModal from '../AlbumPrintPackageOption/components/PrintPackageConfigurableItemsListModal';
import { getPhotoOrientation } from '../PrintProductsGroup/helpers';
import PrintProductGroupConfiguratorModal from '../../../../../../../../../services/prints/components/PrintProductGroupConfigurator/modal';
import { getPrintSizeServiceIds } from '../../../../../../../../../util/print-product-configuration';
import { getPrintOrderServiceLabels } from '../../../../../../../../../util/print-order-configuration';
import { isStandardPrint } from '../../../../../../../../../util/print-product';
import PrintProductSizeRoesConfiguratorModal from '../PrintProductsGroup/components/PrintProductSizeRoesConfigurator';
import { CUSTOM_PRINT_SUBTYPE } from '../../../../../../../../../constants/pro-custom-prints';
import CardElementPhotoSelectionControl from '../../../../../../../../../services/cards/components/CardElementPhoto/CardElementPhotoSelectionControl';
import PrintPackageDigitalDownloadAccountCardModal from '../AlbumPrintPackageOption/components/PrintPackageDigitalDownloadAccountCardModal/PrintPackageDigitalDownloadAccountCardModal';
import {
  isMultiIdentityPackageMembership,
  isMultiPosePackageMembership,
} from '../../../../../../../../temp/views/memberships/AlbumMembershipGroup/helpers';
import useIdentityJerseyColors from '../../../../hooks/useIdentityJerseyColors';
import Label from '../../../../../../../../../components/global/Label';
import SelectablePrintPackageItemsList from './components/SelectablePrintPackageItemsList';
import useAlbumPrintPackageMaxCheckoutItemsConfigurator from './hooks/useAlbumPrintPackageMaxCheckoutItemsConfigurator';
import AddedToCartLabel from '../../../PhotoPurchasingOptions/AddedToCartLabel';

const propTypes = {
  className: PropTypes.string,
  readOnly: PropTypes.bool,
  albumPrintPackage: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  cartItem: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  onAlbumPrintPackageToggle: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  identity: PropTypes.shape({
    id: PropTypes.string,
  }),
  albumMembershipGroup: PropTypes.shape({
    id: PropTypes.string,
  }),
  photo: PropTypes.shape({
    id: PropTypes.string,
  }),
  onCheckout: PropTypes.func,
  isAllPhotos: PropTypes.bool,
  isGreenScreenEnabled: PropTypes.bool,
  wallpaperCustomText: PropTypes.string,
  wallpaperFontSize: PropTypes.number,
};

const PHOTO_SELECTION_STEP = {
  NONE: 'NONE',
  UPSELL: 'UPSELL',
  PHOTO_SELECT: 'PHOTO_SELECT',
};

const AlbumPrintPackageMaxCheckoutItemsOption = ({
  className,
  readOnly = false,
  albumPrintPackage,
  cartItem,
  onAlbumPrintPackageToggle,
  onUpdate,
  identity,
  albumMembershipGroup,
  photo,
  onCheckout,
  isAllPhotos,
  isGreenScreenEnabled,
  watermarkSettings,
}) => {
  const { jerseyColors } = useIdentityJerseyColors({
    identityId: identity?.id,
    albumFolderId:
      albumMembershipGroup?.album_folder?.id ??
      albumMembershipGroup?.album?.root_album_folder?.id,
  });

  const classes = classNames({
    [className]: !!className,
    'purchasing-option-item': true,
    'purchasing-option-item-album-print-package': true,
    'is-selected': !!cartItem,
  });

  const printPackage = albumPrintPackage.print_package;
  const orderServiceLabels = getPrintOrderServiceLabels(
    printPackage.print_order_configuration,
  );
  const [
    configurableItemsListModalOpen,
    setConfigurableItemsListModalOpen,
  ] = useState(false);
  const [
    configurableItemsListModalProps,
    setConfigurableItemsListModalProps,
  ] = useState(null);

  const [photoSelectionStep, setPhotoSelectionStep] = useState(
    PHOTO_SELECTION_STEP.NONE,
  );

  const [
    digitalDownloadAccountCardModalOpen,
    setDigitalDownloadAccountCardModalOpen,
  ] = useState(false);

  const overriddenPrintPackageItem = getAlbumPrintPackageTaggedItem(
    albumPrintPackage,
  );

  const printBackgrounds = isGreenScreenEnabled
    ? getPrintPackagePrintBackgrounds(
        albumPrintPackage,
        albumMembershipGroup,
        jerseyColors,
      )
    : [];

  const allowMultiIdentityPhoto =
    isMultiIdentityPackageMembership(albumMembershipGroup) ?? false;
  const allowMultiPose =
    isMultiPosePackageMembership(albumMembershipGroup) ?? false;

  const {
    activeConfigurableItem,
    activeConfig,
    onConfigurationStart,
    onConfigurationCancel,
    selectedPrintPackageItems,
    configurableItems,
    onItemConfigured,
    onItemsConfigured,
    onConfigurableItemUpdate,
    onPrintPackageItemToggle,
    onAddToCart,
    onResetConfiguration,
  } = useAlbumPrintPackageMaxCheckoutItemsConfigurator({
    albumPrintPackage,
    albumPrintPackageCartItem: cartItem,
    onItemConfiguredComplete: ({
      completeConfigurableItem,
      completeConfigurableItems,
      remainingConfigurableItems,
    }) => {
      if (
        remainingConfigurableItems.length === 0 &&
        overriddenPrintPackageItem
      ) {
        if (overriddenPrintPackageItem.is_optional) {
          setPhotoSelectionStep(PHOTO_SELECTION_STEP.UPSELL);
        } else {
          setPhotoSelectionStep(PHOTO_SELECTION_STEP.PHOTO_SELECT);
        }
      } else {
        setConfigurableItemsListModalProps({
          printPackage,
          completeConfigurableItem,
          remainingConfigurableItems,
          completeConfigurableItems,
        });
      }
    },
    onUpdateCartItem: onUpdate,
    allowMultiPose,
  });

  const remainingItems = configurableItems?.length ?? 0;

  const configurablePrintProductItems = useMemo(
    () =>
      configurableItems
        .filter(
          ({ print_product_configuration: printProductConfig = {} }) =>
            !!printProductConfig?.print_product?.card,
        )
        .sort((a, b) => {
          if (isStandardPrint(a?.print_product_configuration?.print_product)) {
            return -1;
          }
          if (isStandardPrint(b?.print_product_configuration?.print_product)) {
            return 1;
          }

          if (!b?.print_product_configuration?.print_product) {
            return -1;
          }

          if (!a?.print_product_configuration?.print_product) {
            return -1;
          }

          return (
            a?.print_product_configuration?.print_product?.name ?? ''
          ).localeCompare(
            b?.print_product_configuration?.print_product?.name ?? '',
          );
        }),
    [configurableItems],
  );

  const albumFolderTag = overriddenPrintPackageItem?.album_folder_tag;

  return (
    <div className={classes}>
      <div>
        <div className="purchasing-option-item-info-extra">
          <Badge
            badgeStyle="yellow"
            className="purchasing-option-item-info-badge"
          >
            <PriceDisplay
              price={printPackage.price.value}
              currency={printPackage.price.currency}
              trimDecimalZeros={false}
            />
          </Badge>
        </div>

        <div className="purchasing-option-item-thumb">
          <img
            src={
              printPackage?.preview_asset_image?.url ??
              getAsset('/icon-package-3d.svg')
            }
            alt=""
          />
        </div>
      </div>

      <div className="purchasing-option-item-content-wrap">
        <div className="purchasing-option-item-title">{printPackage.name}</div>

        <div className="purchasing-option-item-content">
          <Label
            id={`print-package-max-checkout-items-label-${albumPrintPackage.id}`}
            className="text-dark"
          >
            Pick {printPackage.max_checkout_items} of the following:
          </Label>
          <SelectablePrintPackageItemsList
            albumPrintPackage={albumPrintPackage}
            selectedPrintPackageItems={selectedPrintPackageItems}
            onPrintPackageItemToggle={onPrintPackageItemToggle}
            cartItem={cartItem}
          />

          {printPackage.digital_downloads_mode ===
            DIGITAL_DOWNLOADS_MODE.INCLUDED && (
            <span className="d-block text-sm text-italic text-bold">
              Includes hi&ndash;res download of this photo
            </span>
          )}
          {orderServiceLabels.length > 0 && (
            <span className="d-block text-sm text-italic text-bold">
              Includes {orderServiceLabels.join(', ')}
            </span>
          )}
        </div>

        {!readOnly && (
          <div className="purchasing-option-item-actions">
            <Button
              btnStyle={classNames({
                primary: !cartItem,
                info: !!cartItem,
              })}
              className={classNames({
                'purchasing-options-item-btn': true,
                'btn-purchase-options-in-cart': !!cartItem,
              })}
              onClick={() => {
                if (
                  !cartItem &&
                  selectedPrintPackageItems.find(
                    item =>
                      !!customPrintRequiresConfiguration(
                        item.custom_print,
                        allowMultiPose,
                      ) || !!item.print_product_configuration,
                  )
                ) {
                  onAddToCart();
                } else if (
                  !cartItem &&
                  overriddenPrintPackageItem?.is_optional
                ) {
                  setPhotoSelectionStep(PHOTO_SELECTION_STEP.UPSELL);
                } else if (!cartItem && overriddenPrintPackageItem) {
                  setPhotoSelectionStep(PHOTO_SELECTION_STEP.PHOTO_SELECT);
                }

                // We need to add the item one way or the other.
                onAlbumPrintPackageToggle({ selectedPrintPackageItems });
              }}
              disabled={
                selectedPrintPackageItems.length <
                printPackage.max_checkout_items
              }
              data-testid="btn-album-custom-print"
            >
              {cartItem ? (
                <AddedToCartLabel />
              ) : (
                <Fragment>Add to cart</Fragment>
              )}
            </Button>
          </div>
        )}

        {!readOnly && remainingItems > 0 && cartItem && (
          <Button
            btnStyle="link"
            className="purchasing-options-item-btn mt-1"
            onClick={onConfigurationStart}
          >
            Configure ({remainingItems}) more{' '}
            {pluralize('item', remainingItems)}
          </Button>
        )}

        {!readOnly &&
          printPackageRequiresConfiguration(printPackage, allowMultiPose) &&
          remainingItems === 0 &&
          cartItem && (
            <Button
              btnStyle="link"
              className="purchasing-options-item-btn mt-1"
              onClick={() => {
                onAlbumPrintPackageToggle({ selectedPrintPackageItems });
              }}
            >
              Change selections for this package
            </Button>
          )}

        {!readOnly && remainingItems === 0 && cartItem && (
          <Button
            btnStyle="link"
            className="purchasing-options-item-btn mt-1"
            onClick={() => {
              onUpdate(cartItem.id, {
                ignoreOnPurchasingOptions: true,
              });
              onResetConfiguration();
            }}
          >
            Purchase an additional package
          </Button>
        )}
      </div>

      {isGreenScreenEnabled && (
        <PrintPackageDigitalDownloadAccountCardModal
          forceOpen={digitalDownloadAccountCardModalOpen}
          accountCards={(cartItem?.configurableItems ?? [])
            .filter(item => !!item?.config?.accountCard?.id)
            .map(({ config }) => config.accountCard)}
          onAccountCardSelected={accountCard => {
            onUpdate(cartItem.id, {
              digitalDownloadAccountCard: accountCard,
            });
            setConfigurableItemsListModalProps({
              printPackage,
              remainingConfigurableItems: [],
            });
            setDigitalDownloadAccountCardModalOpen(false);
          }}
        />
      )}
      {overriddenPrintPackageItem && (
        <Fragment>
          <PriceTagPhotoUpsellModal
            forceOpen={photoSelectionStep === PHOTO_SELECTION_STEP.UPSELL}
            printPackageItem={overriddenPrintPackageItem}
            onClose={() => {
              setConfigurableItemsListModalProps({
                printPackage,
                remainingConfigurableItems: [],
              });
              setPhotoSelectionStep(PHOTO_SELECTION_STEP.NONE);
            }}
            onPhotoSelect={() => {
              setPhotoSelectionStep(PHOTO_SELECTION_STEP.PHOTO_SELECT);
            }}
          />
          <AlbumMembershipGroupTaggedPhotosModal
            forceOpen={photoSelectionStep === PHOTO_SELECTION_STEP.PHOTO_SELECT}
            identity={identity}
            albumMembershipGroup={albumMembershipGroup}
            albumFolderTag={albumFolderTag}
            overriddenPrintPackageItem={overriddenPrintPackageItem}
            onPhotoSelect={({ photo: selectedPhoto, accountCard }) => {
              onUpdate(cartItem.id, {
                extendedPrintPackageItems: [
                  {
                    printPackageItem: overriddenPrintPackageItem,
                    photo: selectedPhoto,
                    accountCard,
                  },
                ],
              });
              setConfigurableItemsListModalProps({
                printPackage,
                remainingConfigurableItems: [],
              });
              setPhotoSelectionStep(PHOTO_SELECTION_STEP.NONE);
            }}
            onClose={() => {
              if (!overriddenPrintPackageItem.is_optional) {
                onAlbumPrintPackageToggle({ selectedPrintPackageItems });
              } else {
                setConfigurableItemsListModalProps({
                  printPackage,
                  remainingConfigurableItems: [],
                });
              }
              setPhotoSelectionStep(PHOTO_SELECTION_STEP.NONE);
            }}
            photoIsOptional={overriddenPrintPackageItem.is_optional}
          />
        </Fragment>
      )}
      {configurableItemsListModalOpen && (
        <PrintPackageConfigurableItemsListModal
          forceOpen
          cartItem={cartItem}
          onConfigurableItemUpdate={configurableItem =>
            onConfigurableItemUpdate(configurableItem)
          }
          onClose={() => setConfigurableItemsListModalOpen(false)}
        />
      )}
      {cartItem && (
        // Do not rely on checking for the activeConfigurationItem to be
        // available since it may not have the print_product_size config
        // at the time. Instead, just rely on the photo alone, and go w/
        // usePrintProductConfiguration.js' handlePrintProductSizeSelect
        // which will automatically reset to the available orientation if
        // the requested one is not available.
        <>
          <AccountCardAlbumCustomPrintConfiguratorModal
            forceOpen={
              !!activeConfigurableItem?.custom_print &&
              activeConfigurableItem?.custom_print?.subtype !==
                CUSTOM_PRINT_SUBTYPE.SPECIALTY_PRINT
            }
            albumCustomPrint={{
              custom_print: activeConfigurableItem?.custom_print,
            }}
            onDismiss={onConfigurationCancel}
            onFinished={({ accountCard }) => {
              onItemConfigured({ accountCard });
            }}
            buttonText="Continue"
            identity={identity}
            albumMembershipGroup={albumMembershipGroup}
            deleteAccountCardOnDismiss={!activeConfig?.accountCard}
            defaultAccountCard={activeConfig?.accountCard}
            isAllPhotos={isAllPhotos}
            watermarkSettings={watermarkSettings}
            photo={photo}
          />
          <CardElementPhotoSelectionControl
            uploadDisabled
            title={`Select ${pluralize(
              'photo',
              activeConfigurableItem?.custom_print?.photos_no,
              true,
            )} for: ${activeConfigurableItem?.custom_print?.name}`}
            photosContext={{
              album_id:
                albumMembershipGroup &&
                (albumMembershipGroup.is_album
                  ? albumMembershipGroup.id
                  : undefined),
              album_folder_id:
                albumMembershipGroup &&
                (!albumMembershipGroup.is_album
                  ? albumMembershipGroup.id
                  : undefined),
              root_album_folder_id:
                albumMembershipGroup && albumMembershipGroup.is_album
                  ? albumMembershipGroup.album.root_album_folder?.id
                  : undefined,
              // We rely on a resolver that the membershipGroup would have by
              // default when we don't create a pseudo membership (identities).
              // This allows us to actually discern if this is a membership or not.
              is_album_membership_group: !!albumMembershipGroup?.identities,
              is_all_photos: isAllPhotos,
              identity_id: allowMultiIdentityPhoto ? undefined : identity?.id,
            }}
            forceOpen={
              !!activeConfigurableItem?.custom_print &&
              activeConfigurableItem?.custom_print?.subtype ===
                CUSTOM_PRINT_SUBTYPE.SPECIALTY_PRINT
            }
            onClose={onConfigurationCancel}
            maxPhotosCount={activeConfigurableItem?.custom_print?.photos_no}
            currentSelectedPhotos={activeConfig?.photos ?? [photo]}
            currentSelectedPhoto={
              allowMultiPose &&
              activeConfigurableItem?.custom_print?.photos_no === 1
                ? photo
                : undefined
            }
            onChange={photos => {
              onItemConfigured({
                photos: photos?.id
                  ? [{ photo: photos, position: 0 }]
                  : photos.map((selectedPhoto, index) => ({
                      photo: selectedPhoto,
                      position: index,
                    })),
              });
            }}
            watermarkSettings={watermarkSettings}
          />

          {activeConfigurableItem?.print_product_configuration && (
            <>
              {!activeConfig ? (
                <PrintProductGroupConfiguratorModal
                  forceOpen
                  printProducts={configurablePrintProductItems.map(
                    printProductItem => ({
                      id: printProductItem?.id,
                      printProductId:
                        printProductItem?.print_product_configuration
                          .print_product.id,
                      printProductName:
                        printProductItem?.print_product_configuration
                          .print_product.name,
                      printProductSizeId:
                        printProductItem?.print_product_configuration
                          .print_product_size.id,
                      printProductSizeName:
                        printProductItem?.print_product_configuration
                          .print_product_size.print_size.name,
                      printProductSizeServiceIds: getPrintSizeServiceIds(
                        printProductItem?.print_product_configuration,
                      ),
                      orientation: getPhotoOrientation(photo),
                      // defaultAccountCard: technically supported, but we're not editing
                      // on this modal as of now. If we do, we'll need to get each config
                      // from the cart Item.
                    }),
                  )}
                  photo={photo}
                  onClose={() => {
                    onConfigurationCancel();
                  }}
                  onComplete={configurations => {
                    onItemsConfigured(configurations);
                  }}
                  buttonText="Continue"
                  albumMembershipGroup={albumMembershipGroup}
                  identity={identity}
                  printBackgrounds={printBackgrounds}
                  watermarkSettings={watermarkSettings}
                />
              ) : (
                <>
                  <PrintProductConfiguratorModal
                    forceOpen={
                      activeConfigurableItem?.print_product_configuration
                        ?.print_product?.card?.id
                    }
                    printProductId={
                      activeConfigurableItem?.print_product_configuration
                        ?.print_product.id
                    }
                    printProductSizeId={
                      activeConfigurableItem?.print_product_configuration
                        ?.print_product_size.id
                    }
                    printProductSizeServiceIds={getPrintSizeServiceIds(
                      activeConfigurableItem?.print_product_configuration,
                    )}
                    defaultAccountCard={activeConfig?.accountCard}
                    orientation={getPhotoOrientation(photo)}
                    photo={photo}
                    onClose={() => {
                      onConfigurationCancel();
                    }}
                    onComplete={({ accountCard }) => {
                      onItemConfigured({ accountCard });
                    }}
                    controlsDisabled
                    albumMembershipGroup={albumMembershipGroup}
                    identity={identity}
                    printBackgrounds={printBackgrounds}
                    forPrintPackages
                    watermarkSettings={watermarkSettings}
                  />
                  <PrintProductSizeRoesConfiguratorModal
                    forceOpen={
                      !activeConfigurableItem?.print_product_configuration
                        ?.print_product?.card?.id
                    }
                    printProduct={
                      activeConfigurableItem?.print_product_configuration
                        ?.print_product
                    }
                    printProductSize={
                      activeConfigurableItem?.print_product_configuration
                        ?.print_product_size
                    }
                    photo={photo}
                    identity={identity}
                    onComplete={({ roesConfiguration }) => {
                      onItemConfigured({ roesConfiguration });
                    }}
                    albumMembershipGroup={albumMembershipGroup}
                    isAllPhotos={isAllPhotos}
                    onClose={() => onConfigurationCancel()}
                    watermarkSettings={watermarkSettings}
                  />
                </>
              )}
            </>
          )}
        </>
      )}

      {!!configurableItemsListModalProps && (
        <PrintPackageConfigurableItemCompleteModal
          forceOpen
          onCheckout={onCheckout}
          onConfigureNextItem={onConfigurationStart}
          {...configurableItemsListModalProps}
          onClose={() => {
            setConfigurableItemsListModalProps(null);
          }}
        />
      )}
    </div>
  );
};

AlbumPrintPackageMaxCheckoutItemsOption.propTypes = propTypes;

export default AlbumPrintPackageMaxCheckoutItemsOption;
